import { Injectable, signal } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  createUpdatePropertyListing,
  deleteProperty,
  getAllAmenities,
  getAllPropertyTypes,
  getBookings,
  getHomePageListings,
  getHomePageListingsSuccess,
  getPropertyDetails,
  setPropertyStatus,
  toggleAvailability,
  uploadFile,
  uploadFileFailure,
  uploadFileSuccess,
} from 'src/app/store/listings/listings-actions';
import {
  selectAmenities,
  selectBookings,
  selectIsLoading,
  selectListings,
  selectPropertyDetails,
  selectPropertyTypes,
} from 'src/app/store/listings/listings-selector';
import { AdvancedSearchPayload, Property } from 'src/app/store/listings/listings.models';
import { LoaderService } from '../loader-service/loader.service';
import {
  Bookings,
  PropertyImageType,
  PropertyListing,
  PropertyStatusData,
  ToggleAvailability,
} from 'src/app/shared/models';
import { merge } from 'rxjs';
import { selectPaginationData } from 'src/app/store/shared/selectors';
import { UserFilterWithPagination } from 'src/app/models/request-models/user-filter-with-pagination-request';
import { getAllUsers } from 'src/app/store/users/actions';
import { selectUsersList } from 'src/app/store/users/selectors';
import { ApplicationApiUrls } from 'src/app/constants/api-url-constants';
import { selectWishlistItems } from 'src/app/store/wishlist/selectors';
import {
  addGuestWishlistItem,
  removeGuestWishlistItem,
  addGuestWishlistItemFailure,
  addGuestWishlistItemSuccess,
  removeGuestWishlistItemFailure,
  removeGuestWishlistItemSuccess,
} from 'src/app/store/wishlist/actions';
import { AuthFacadeService } from './auth-facade.service';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UserFacadeService {
  listProperties$ = this.store.select(selectListings);
  amenities$ = this.store.select(selectAmenities);
  propertyTypes$ = this.store.select(selectPropertyTypes);
  propertyDetails$ = this.store.select(selectPropertyDetails);
  isLoading$ = this.store.select(selectIsLoading);
  pagination$ = this.store.select(selectPaginationData);
  usersList$ = this.store.select(selectUsersList);
  bookings$ = this.store.select(selectBookings);
  guestWishlistItems$ = this.store.select(selectWishlistItems);
  role$: import('rxjs').Observable<'guest' | 'host'> = this.authFacade.role$.pipe(
    map((r) => (r === 'Host' ? 'host' : 'guest')),
  );

  pendingAddSig = signal<Set<number>>(new Set<number>());
  pendingRemoveSig = signal<Set<number>>(new Set<number>());

  onSuccess$ = merge(this.actions$.pipe(ofType(uploadFileSuccess)));

  onError$ = merge(this.actions$.pipe(ofType(uploadFileFailure)));

  wishlistOps$ = merge(
    this.actions$.pipe(
      ofType(
        addGuestWishlistItemSuccess,
        removeGuestWishlistItemSuccess,
        addGuestWishlistItemFailure,
        removeGuestWishlistItemFailure,
      ),
    ),
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private loaderService: LoaderService,
    private authFacade: AuthFacadeService,
  ) {}

  getListings(data: Partial<AdvancedSearchPayload>, isLazyLoad = false) {
    this.store.dispatch(getHomePageListings({ data, isLazyLoad }));
  }

  clearListings() {
    this.store.dispatch(
      getHomePageListingsSuccess({
        data: {
          result: [],
          successful: true,
          totalPages: 0,
          totalRecords: 0,
          message: '',
          pageNumber: 0,
          pageSize: 0,
        },
      }),
    );
  }

  getAllAmenities() {
    this.store.dispatch(getAllAmenities());
  }

  getAllPropertyTypes() {
    this.store.dispatch(getAllPropertyTypes());
  }

  createUpdatePropertyListing(data: Partial<PropertyListing>, isDraft: boolean) {
    this.store.dispatch(createUpdatePropertyListing({ data, isDraft }));
  }

  uploadFile(file: File, imageType: PropertyImageType) {
    this.store.dispatch(uploadFile({ file, imageType }));
  }

  getPropertyDetails(propertyId: number) {
    this.store.dispatch(getPropertyDetails({ propertyId }));
  }

  savePendingListings(data: Partial<PropertyListing>) {
    this.store.dispatch(createUpdatePropertyListing({ data, isDraft: true }));
  }

  setPropertyStatus(data: PropertyStatusData, searchData: Partial<AdvancedSearchPayload>) {
    this.store.dispatch(setPropertyStatus({ data, searchData }));
  }

  deleteProperty(propertyId: number, searchData: Partial<AdvancedSearchPayload>) {
    this.store.dispatch(deleteProperty({ propertyId, searchData }));
  }

  getAllUsers(data: Partial<UserFilterWithPagination>) {
    this.store.dispatch(getAllUsers({ data }));
  }

  getBookings(data: Bookings, url: ApplicationApiUrls) {
    this.store.dispatch(getBookings({ data, url }));
  }

  toggleAvailability(data: ToggleAvailability, searchData: Partial<AdvancedSearchPayload>) {
    this.store.dispatch(toggleAvailability({ data, searchData }));
  }
  addToWishlist(role: 'guest' | 'host', locationKey: string, propertyId: number) {
    this.store.dispatch(addGuestWishlistItem({ propertyId }));
  }

  removeFromWishlist(role: 'guest' | 'host', locationKey: string, propertyId: number) {
    this.store.dispatch(removeGuestWishlistItem({ propertyId }));
  }

  markPendingAdd(propertyId: number) {
    this.pendingAddSig.update((set) => {
      const s = new Set(set);
      s.add(propertyId);
      return s;
    });
  }

  markPendingRemove(propertyId: number) {
    this.pendingRemoveSig.update((set) => {
      const s = new Set(set);
      s.add(propertyId);
      return s;
    });
  }

  clearPendingByPropertyId(propertyId: number) {
    this.pendingAddSig.update((set) => {
      const s = new Set(set);
      s.delete(propertyId);
      return s;
    });
    this.pendingRemoveSig.update((set) => {
      const s = new Set(set);
      s.delete(propertyId);
      return s;
    });
  }
}
